import { useEffect, useRef } from "react";
import { turnstileVerification } from "./turnstile-adapter";
import type { HumanVerification } from "./types";

export interface HumanChallengeProps {
  sitekey: string;
  /** Fired with the single-use token once solved — the app resubmits the original request with it. */
  onVerified: (token: string) => void;
  /** Fired on a render/verify failure — the app shows "try again shortly" (never a silent pass, R1). */
  onError?: (reason: string) => void;
  language?: string;
  /** Injectable for tests (default = the real Turnstile adapter). */
  verification?: HumanVerification;
}

/**
 * The escalation-only challenge widget (booking-abuse-protection). Rendered ONLY after the backend
 * returns `CHALLENGE_REQUIRED`; mounts the [HumanVerification] port into a container and forwards the
 * solved token. The vendor (Turnstile) lives behind the port — this component references no vendor script.
 */
export function HumanChallenge({
  sitekey,
  onVerified,
  onError,
  language,
  verification = turnstileVerification,
}: HumanChallengeProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest callbacks in refs so the effect depends only on the stable challenge identity
  // (sitekey/language/adapter) and never re-mounts when a parent passes fresh inline handlers. Written
  // from an effect (not during render) — refs must not be written during render.
  const onVerifiedRef = useRef(onVerified);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const handle = verification.render(container, {
      sitekey,
      language,
      onToken: (token) => onVerifiedRef.current(token),
      onError: (reason) => onErrorRef.current?.(reason),
    });
    return () => handle.remove();
  }, [sitekey, language, verification]);

  return <div ref={ref} data-testid="human-challenge" />;
}
